import { get } from 'lodash';
import Lottie from 'lottie-react-web';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, TextInput, TouchableOpacity } from 'react-native';
import Modal from 'react-native-modal';

import { IconSvg } from 'src/assets';
import { RatingReservationPayloadProps } from 'src/common/api/request';
import { useAppDispatch } from 'src/common/hooks';
import useNativeSafeInsets from 'src/common/hooks/useNativeSafeInsets';
import { useRateReservationMutation } from 'src/common/hooks/useRateReservationMutation';
import { Container, IconButton, LoadingModalHOC, PrimaryButton, RegularText, Row, SizedBox } from 'src/components';
import { loadingHide, loadingShow } from 'src/redux/loadingModal/LoadingModalSlice';
import { AppColors } from 'src/themes/Colors';
import { metrics, pixelScaler } from 'src/themes/Metrics';

export const RatingAndReviewModal = ({
  operatorId,
  reservationId,
  isVisible,
  onClose,
}: {
  operatorId: string;
  reservationId: string;
  isVisible: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const insets = useNativeSafeInsets();
  const dispatch = useAppDispatch();
  const { mutateAsync, isError, error } = useRateReservationMutation();
  const [rating, setRating] = React.useState<number>(0);
  const [comment, setComment] = React.useState<string>('');
  const [successMessage, setSuccessMessage] = React.useState<string>('');

  const handleSubmitPress = async () => {
    try {
      dispatch(loadingShow());
      const payload: RatingReservationPayloadProps = {
        operatorId,
        reservationId,
        rating,
        comment,
      };
      const response = await mutateAsync(payload);
      console.log();
      dispatch(loadingHide());
      setSuccessMessage(response.data.data.message);
      console.log({ payload });
    } catch (error) {
      // console.log({ error });

      // error.response.data.errors;

      dispatch(loadingHide());
    }
    // onClose();
  };

  const handleClose = () => {
    setSuccessMessage('');
    onClose();
  };

  return (
    <Modal
      style={{ margin: 0, justifyContent: 'flex-end' }}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      isVisible={isVisible}>
      <LoadingModalHOC>
        <Container
          padding={metrics.medium}
          flex
          backgroundColor={AppColors.white}
          paddingTop={metrics.medium + insets.top}
          paddingBottom={metrics.medium + insets.bottom}>
          <IconButton icon={<IconSvg.Close fill={AppColors.black} />} onPress={onClose} />
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              paddingVertical: metrics.medium,
              paddingHorizontal: metrics.huge,
            }}>
            <RegularText style={{ marginBottom: metrics.small }} textAlign="center" fontWeight="bold" fontSize="huge">
              {t('howWasYourTrip')}
            </RegularText>
            <RegularText textAlign="center" color={AppColors.gray}>
              {t('yourFeedbackMeansAlotToUs')}
            </RegularText>
            <SizedBox />
            <Row style={{ justifyContent: 'space-between' }} marginBottom={metrics.large}>
              <RatingButton value={1} rating={rating} onRating={setRating} />
              <RatingButton value={2} rating={rating} onRating={setRating} />
              <RatingButton value={3} rating={rating} onRating={setRating} />
              <RatingButton value={4} rating={rating} onRating={setRating} />
              <RatingButton value={5} rating={rating} onRating={setRating} />
            </Row>
            <TextInput
              multiline
              value={comment}
              onChangeText={setComment}
              placeholder="Your Review..."
              placeholderTextColor={AppColors.gray}
              style={{
                height: pixelScaler(83),
                padding: metrics.medium,
                borderRadius: metrics.borderRadius,
                borderWidth: 1,
                borderColor: isError ? AppColors.red : AppColors.border,
                backgroundColor: AppColors.ratingBackground,
              }}
            />
            {isError && (
              <Container marginTop={metrics.medium}>
                <RegularText textAlign="right" fontSize="medium" color={AppColors.red}>
                  {get(error, "response.data.errors['comment']", '')}
                </RegularText>
              </Container>
            )}
            <SizedBox height="huge" />
            {/* {rating > 0 && (
            <>
              <RegularText
                style={{ marginBottom: metrics.small }}
                textAlign="center"
                fontWeight="bold"
                fontSize="larger">
                {t('howWasYourExperienceWith')}
              </RegularText>
              <RegularText textAlign="center" color={AppColors.gray}>
                Nham24 Express
              </RegularText>
              <SizedBox />
              <CustomSlider label="Safety" />
              <CustomSlider label="Cleanliness" />
              <CustomSlider label="Punctuality" />
              <CustomSlider label="Hospitality" />
            </>
          )} */}
          </ScrollView>
          {rating > 0 && <PrimaryButton style={{ borderRadius: 50 }} text="submit" onPress={handleSubmitPress} />}
        </Container>
        <Modal isVisible={successMessage.length > 0}>
          <Container
            borderRadius={metrics.borderRadius}
            paddingVertical={metrics.huge}
            paddingHorizontal={metrics.huge}
            marginHorizontal={metrics.large}
            backgroundColor="white">
            <Lottie
              height={pixelScaler(110)}
              width={pixelScaler(110)}
              options={{
                loop: true,
                animationData: require('src/assets/lotties/rate-success.json'),
              }}
            />
            <RegularText textAlign="center" fontWeight="bold" fontSize="huge" textLang="ratingSuccess" />
            <SizedBox height="medium" />
            <RegularText textAlign="center" fontSize="medium" textLang="paymentSuccessContent" />
            <SizedBox height="huge" />

            <PrimaryButton onPress={handleClose} text="close" />
          </Container>
        </Modal>
      </LoadingModalHOC>
    </Modal>
  );
};

const RatingButton = ({
  value,
  rating,
  onRating,
}: {
  value: number;
  rating: number;
  onRating: (rate: number) => void;
}) => {
  return (
    <TouchableOpacity activeOpacity={0.8} onPress={() => onRating(value)}>
      {value === 1 && (
        <IconSvg.Rate.Rate1 fill={rating === value ? AppColors.rateColor[rating] : AppColors.rateUnselect} />
      )}
      {value === 2 && (
        <IconSvg.Rate.Rate2 fill={rating === value ? AppColors.rateColor[rating] : AppColors.rateUnselect} />
      )}
      {value === 3 && (
        <IconSvg.Rate.Rate3 fill={rating === value ? AppColors.rateColor[rating] : AppColors.rateUnselect} />
      )}
      {value === 4 && (
        <IconSvg.Rate.Rate4 fill={rating === value ? AppColors.rateColor[rating] : AppColors.rateUnselect} />
      )}
      {value === 5 && (
        <IconSvg.Rate.Rate5 fill={rating === value ? AppColors.rateColor[rating] : AppColors.rateUnselect} />
      )}
    </TouchableOpacity>
  );
};
